import type { StraightenerRecipeValueRow } from "../../../production/straightener-recipes/models/StraightenerRecipe";
import type {
	GroupRowBase,
	SummaryPropertyDef,
} from "../../information-retrieval/models/InformationRetrieval";

/**
 * One row per roll per date/shift group, as returned by
 * [MILL].[GetStraightenerInformationRetrievalDataWeb]. `Date` and `Shift` are
 * null when the corresponding grouping is off.
 */
export interface StraightenerInformationRetrievalRow {
	Date: string | null;
	Shift: string | null;
	RollNumber: number;
	RunsCount: number;
	ActualCount: number;
	ActualSum: number | null;
	ActualAvg: number | null;
	/** Lowest and highest value across the runs of the group. */
	ActualMin: number | null;
	ActualMax: number | null;
	ActualSumSquares: number | null;
	RecipeCount: number;
	TargetSum: number | null;
	TargetAvg: number | null;
	RecipeMinCount: number;
	RecipeMinSum: number | null;
	RecipeMinAvg: number | null;
	RecipeMaxCount: number;
	RecipeMaxSum: number | null;
	RecipeMaxAvg: number | null;
	/** Runs with an actual value and both recipe limits. */
	BandCount: number;
	OutOfLimitsCount: number;
	/** Null when the group ran without a current recipe, blank when under several. */
	RecipeName: string | null;
}

export interface StraightenerInformationRetrievalFilters {
	start: string;
	end: string;
	productTypeId: number | null;
	productSizeId: number | null;
	/** `'0'` = all shifts, `'-'` = current shift, otherwise the crew letter. */
	shiftId: string;
	groupByDate: boolean;
	groupByShift: boolean;
}

/** How steady one proposed value was across the runs it was built from. */
export interface RecipeProposalStat {
	ParameterCode: string;
	ParameterName: string;
	GroupCode: string;
	RollNumber: number;
	Runs: number;
	ModeValue: number;
	/** Percent of the runs that used ModeValue. */
	ModeShare: number;
	AvgValue: number;
	MinValue: number;
	MaxValue: number;
}

export interface RecipeProposalSummary {
	Runs: number;
	FirstRun: string;
	LastRun: string;
}

export interface RecipeProposal {
	values: StraightenerRecipeValueRow[];
	stats: RecipeProposalStat[];
	summary: RecipeProposalSummary | null;
}

export interface RecipeProposalRequest {
	start: string;
	end: string;
	productSizeId: number;
	shiftId: string;
	/** Ticked date/shift groups; a null part matches any. */
	groups: { date: string | null; shift: string | null }[];
}

export interface StraightenerGroupRow extends GroupRowBase {
	rows: StraightenerInformationRetrievalRow[];
}

/** Rows of the aggregate grid. Keys are shared with the mill page. */
export const STRAIGHTENER_SUMMARY_PROPERTIES: readonly SummaryPropertyDef[] = [
	{
		key: "actual",
		label: "Actual Break (avg)",
		description:
			"Break the roll actually ran at, averaged over the selected runs",
		legendColor: "#244200",
		format: "FLOAT3",
	},
	{
		key: "target",
		label: "Recipe Start Point",
		description: "Start Point of the recipe, averaged over the selected runs",
		legendColor: "#2A32FF",
		format: "FLOAT3",
	},
	{
		key: "maxActual",
		label: "Actual Break (max)",
		description: "Highest Break among the selected runs",
		legendColor: "#D800E6",
		format: "FLOAT3",
	},
	{
		key: "minActual",
		label: "Actual Break (min)",
		description: "Lowest Break among the selected runs",
		legendColor: "#D800E6",
		format: "FLOAT3",
	},
	{
		key: "maxRecipe",
		label: "Recipe Max",
		description: "Upper limit of the recipe Start Point",
		legendColor: "#E60000",
		format: "FLOAT3",
	},
	{
		key: "minRecipe",
		label: "Recipe Min",
		description: "Lower limit of the recipe Start Point",
		legendColor: "#E60000",
		format: "FLOAT3",
	},
	{
		key: "percOutOfLimits",
		label: "% out of limits",
		description:
			"Share of the runs with recipe limits whose Break fell outside them",
		format: "PERCENT",
	},
	{
		key: "stdWithRecipe",
		label: "STD",
		description: "Standard deviation of the Break across the selected runs",
		format: "FLOAT3",
	},
];
